import React, { useState, useEffect } from 'react';
import { H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Row, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../../Api/ApiMethods';
import ArbitrationExpenseList from './ArbitrationExpenseList';
import axios from 'axios';

const ArbitrationInformations = ({ arbitrationId, arbitrationData, refreshArbitrationData }) => {
    const [arbitrationInfoData, setArbitrationInfoData] = useState({
        arbitration: [],
        applicationDate: '',
        caseNumber: '',
        priceForNow: '',
        arbitrationApplicationFee: '',
        expenses: [],
        comments: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('add'); // 'add' or 'modify'
    const [arbitrationInfoId, setArbitrationInfoId] = useState(null);

    const arbitrationInfoApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ArbitrationInformation';

    useEffect(() => {
        console.log('ArbitrationInformations useEffect triggered:', { arbitrationId, arbitrationData });
        if (arbitrationId && arbitrationData && typeof arbitrationData === 'object') {
            console.log('ArbitrationInformations: Calling fetchArbitrationInfoData');
            fetchArbitrationInfoData();
        } else {
            console.log('ArbitrationInformations: Conditions not met', { 
                hasArbitrationId: !!arbitrationId, 
                hasArbitrationData: !!arbitrationData,
                isObjectArbitrationData: typeof arbitrationData === 'object',
                arbitrationDataKeys: arbitrationData && typeof arbitrationData === 'object' ? Object.keys(arbitrationData) : []
            });
            // Eğer arbitration data henüz yüklenmemişse loading'i false yap
            if (arbitrationId && (!arbitrationData || typeof arbitrationData !== 'object')) {
                setLoading(false);
            }
        }
    }, [arbitrationId, arbitrationData]);

    const fetchArbitrationInfoData = async () => {
        try {
            console.log('fetchArbitrationInfoData started, arbitrationData:', arbitrationData);
            setLoading(true);
            
            // Arbitration verilerinden arbitrationInformation nesnesini al
            const arbitrationInformation = arbitrationData && typeof arbitrationData === 'object' 
                ? arbitrationData.arbitrationInformation 
                : null;
            console.log('arbitrationInformation from arbitrationData:', arbitrationInformation);
            
            if (arbitrationInformation && arbitrationInformation.id) {
                // Eğer arbitrationInformation varsa, modify modunda çalış
                setMode('modify');
                setArbitrationInfoId(arbitrationInformation.id);
                
                // Arbitration Information verileri zaten arbitrationData içinde, API çağrısı yapma
                console.log('Using existing arbitrationInformation data:', arbitrationInformation);

                // Tarih formatlaması için helper function
                const formatDateForInput = (dateString) => {
                    if (!dateString) return '';
                    const date = new Date(dateString);
                    return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm formatı
                };

                // API'den gelen veri formatını forma uygun hale getir
                const commentText = arbitrationInformation.comments && Array.isArray(arbitrationInformation.comments) && arbitrationInformation.comments.length > 0 
                    ? arbitrationInformation.comments[0]?.comment || ''
                    : '';

                setArbitrationInfoData({
                    id: arbitrationInformation.id || null,
                    arbitration: arbitrationInformation.arbitration || [arbitrationId],
                    applicationDate: formatDateForInput(arbitrationInformation.applicationDate),
                    caseNumber: arbitrationInformation.caseNumber || '',
                    priceForNow: arbitrationInformation.priceForNow || '',
                    arbitrationApplicationFee: arbitrationInformation.arbitrationApplicationFee || '',
                    expenses: arbitrationInformation.expenses || [],
                    comments: commentText
                });
            } else {
                // ArbitrationInformation yoksa, add modunda çalış
                console.log('No arbitrationInformation found, setting add mode');
                setMode('add');
                setArbitrationInfoId(null);
                setArbitrationInfoData({
                    arbitration: [arbitrationId],
                    applicationDate: '',
                    caseNumber: '',
                    priceForNow: '',
                    arbitrationApplicationFee: '',
                    expenses: [],
                    comments: ''
                });
            }
        } catch (error) {
            console.error('Error processing arbitration information data:', error);
            toast.error('Tahkim bilgileri yüklenirken hata oluştu');
            // Hata durumunda add moduna geç
            setMode('add');
            setArbitrationInfoId(null);
            setArbitrationInfoData({
                arbitration: [arbitrationId],
                applicationDate: '',
                caseNumber: '',
                priceForNow: '',
                arbitrationApplicationFee: '',
                expenses: [],
                comments: ''
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        
        if (type === 'number') {
            // Sayısal alanlar için boş string veya geçerli sayı kontrolü
            const numericValue = value === '' ? '' : Number(value);
            setArbitrationInfoData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setArbitrationInfoData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setIsSubmitting(true);
        
        try {
            // API'ye gönderilecek veriyi API yapısına uygun şekilde hazırla
            const apiData = {
                ...arbitrationInfoData,
                arbitration: [arbitrationId], // arbitrationId'yi array olarak gönder
                // Tarih formatını API'nin beklediği formata çevir
                applicationDate: arbitrationInfoData.applicationDate ? new Date(arbitrationInfoData.applicationDate).toISOString() : null,
                // Comments'i API'nin beklediği formata çevir
                comments: arbitrationInfoData.comments ? [{ 
                    commentType: "Genel",
                    comment: arbitrationInfoData.comments 
                }] : []
            };

            console.log('ArbitrationInformations: API Data being sent:', apiData);

            await handleAddModifyOperation({
                apiEndpoint: arbitrationInfoApiUrl,
                selectedItem: mode === 'modify' ? { id: arbitrationInfoId } : null,
                mode,
                data: apiData,
                refreshDataCallback: () => {
                    // Arbitration verilerini yenile ki arbitrationInformation ID güncellensin
                    refreshArbitrationData();
                    // Component verilerini de yenile
                    fetchArbitrationInfoData();
                },
                componentName: 'Tahkim bilgisi'
            });

        } catch (error) {
            console.error('ArbitrationInformations: Submit error:', error);
            toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Row className="mt-4">
                <Col sm='12'>
                    <Card>
                        <CardHeader>
                            <H5>Tahkim Bilgileri</H5>
                        </CardHeader>
                        <CardBody>
                            <div className="text-center p-4">
                                <p>Yükleniyor...</p>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        );
    }

    return (
        <Row className="mt-4">
            <Col sm='12'>
                <Card>
                    <CardHeader>
                        <H5>
                            <i className="fa fa-balance-scale me-2"></i>
                            Tahkim Bilgileri
                        </H5>
                        <span>
                            {mode === 'modify' 
                                ? 'Mevcut tahkim bilgilerini düzenleyebilirsiniz.' 
                                : 'Tahkim süreci için gerekli bilgileri girebilirsiniz.'
                            }
                        </span>
                    </CardHeader>
                    <CardBody>
                        {mode === 'modify' && (
                            <Alert color="info" className="mb-4">
                                <i className="fa fa-info-circle me-2"></i>
                                Bu tahkim için daha önce tahkim bilgisi kaydedilmiş. Aşağıdaki formdan düzenleyebilirsiniz.
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="applicationDate">
                                            <i className="fa fa-calendar me-1"></i>
                                            Başvuru Tarihi <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="datetime-local"
                                            name="applicationDate"
                                            id="applicationDate"
                                            value={arbitrationInfoData.applicationDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <small className="text-muted">Tahkim başvurusunun yapıldığı tarih ve saat</small>
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="caseNumber">
                                            <i className="fa fa-hashtag me-1"></i>
                                            Dava Numarası <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            name="caseNumber"
                                            id="caseNumber"
                                            value={arbitrationInfoData.caseNumber}
                                            onChange={handleInputChange}
                                            placeholder="Örn: ESN10000"
                                            required
                                        />
                                        <small className="text-muted">Tahkim sürecine ait benzersiz dava numarası</small>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="priceForNow">
                                            <i className="fa fa-money me-1"></i>
                                            Güncel Tutar (₺) <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            name="priceForNow"
                                            id="priceForNow"
                                            value={arbitrationInfoData.priceForNow}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 10000"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <small className="text-muted">Tahkim sürecinde talep edilen güncel tutar</small>
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="arbitrationApplicationFee">
                                            <i className="fa fa-credit-card me-1"></i>
                                            Tahkim Başvuru Ücreti (₺) <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            name="arbitrationApplicationFee"
                                            id="arbitrationApplicationFee"
                                            value={arbitrationInfoData.arbitrationApplicationFee}
                                            onChange={handleInputChange}
                                            placeholder="Örn: 1000"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <small className="text-muted">Tahkim başvurusu için ödenen ücret</small>
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Tahkim masraflarını detaylı göster */}
                            {mode === 'modify' && arbitrationInfoData.expenses && arbitrationInfoData.expenses.length > 0 && (
                                <Row className="mt-4">
                                    <Col md="12">
                                        <ArbitrationExpenseList expenseIds={arbitrationInfoData.expenses} />
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col md="12">
                                    <FormGroup className="mb-4">
                                        <Label for="comments">
                                            <i className="fa fa-comment me-1"></i>
                                            Yorum
                                        </Label>
                                        <Input
                                            type="textarea"
                                            name="comments"
                                            id="comments"
                                            rows="4"
                                            value={arbitrationInfoData.comments}
                                            onChange={handleInputChange}
                                            placeholder="Tahkim süreci ile ilgili yorumlarınızı giriniz..."
                                        />
                                        <small className="text-muted">Tahkim süreci hakkında ek bilgi ve yorumlar</small>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="12" className="text-end">
                                    <Button 
                                        color="primary" 
                                        type="submit" 
                                        disabled={isSubmitting}
                                        size="lg"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <i className="fa fa-spinner fa-spin me-2"></i>
                                                Kaydediliyor...
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fa ${mode === 'add' ? 'fa-save' : 'fa-edit'} me-2`}></i>
                                                {mode === 'add' ? 'Kaydet' : 'Güncelle'}
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default ArbitrationInformations;
